import qs from 'query-string';
import { format as formatDate, parse as parseDate } from 'date-fns';
import invariant from 'invariant';
import { isValidNumber } from 'libphonenumber-js';

import * as actionTypes from './actionTypes';
import xws from 'api/xws/';
import { EMAIL_REGEX } from 'utils/regex';

// Actions
import { setYears, getYears } from 'store/reducers/years/actions';
import { setTrims, getTrims } from 'store/reducers/trims/actions';
import { setModels, getModels } from 'store/reducers/models/actions';
import { loadServices } from 'store/reducers/service/actions';
import { goTo } from 'store/reducers/navigation/actions';
import { ajaxCallbacks } from 'store/reducers/ajaxCall/actions';
import { gaClickTrackingEvent } from 'store/reducers/ga/actions';
import {
  clickTrackingEvent,
  triggerShiftEvent
} from 'store/reducers/clickTracking/actions';
import {
  gtmUserEventPackageAdded,
  gtmSetDimensionConfirmationKey,
  gtmGenericEvent,
  gtmThirdPartyValetConvertionEvent
} from 'store/reducers/gtm/actions';
import { getTransportationOptions } from 'store/reducers/apptOptions/actions';
import {
  modifyEnabled,
  apptNotModified,
  setIdkSelected,
  setSearchCriteriaFindMe,
  setModelLabelOther,
  setMakeLabelOther,
  setSavedMobile
} from 'store/reducers/userInteractions/actions';
import {
  updatePackageInfo,
  clearPackages,
  getPackages,
  setExactMileageFlag
} from 'store/reducers/packages/actions';
import {
  getPromoList,
  setActivePromotion,
  getAnonymousPromotion
} from 'store/reducers/promotions/actions';
import {
  setContactInfo,
  setFirstName,
  setLastName,
  setEmail,
  setPhoneNumber,
  setMobilePhoneNumber,
  setObfuscatedBy,
  setPersonId,
  setExtId
} from './contactInfo/actions';
import actions, {
  updatePrivacySettings,
  updateUserVehicles,
  updateProfile,
  getCustomerPreferences
} from 'store/reducers/user/personInfo/actions';
import {
  resetQuoteAppt,
  updateQuoteServices,
  setReadOnlyPackage as setQuoteReadOnlyPackage,
  setReadOnlyServices as setQuoteReadOnlyServices,
  setQuoteConfirmationId
} from 'store/reducers/quoteAppt/actions';
import { setValetDetails } from './valetDetails/actions';
import { setApptServicePoint as setQuoteApptServicePoint } from 'store/reducers/appointment/actions';
import { setGlobalRepairServices } from 'store/reducers/globalRepair/actions';

// Selectors
import { getWebkey, getQueryParam } from 'store/reducers/queryParams/selectors';
import { getMileOne } from 'store/reducers/dealerData/selectors';
import { getShiftServiceApptSetObj } from 'store/reducers/clickTracking/selectors';
import {
  findMake,
  getPolicyProperty,
  getDealerCountryCode,
  getTellusmoreSvcId,
  isValetOrLoanerIntegration,
  isRecaptchaEnabledOnRescheduleAppt,
  isRecaptchaEnabledOnConfirmAppt,
  isRecaptchaEnabledOnCancelAppt,
  getRecaptchaToken
} from 'store/reducers/dealerSettings/selectors';
import {
  dealerLocaleSelector,
  getLocaleCode
} from 'store/reducers/dealerLocale/selectors';
import {
  getTeamIds,
  getStartTime,
  getVehicle,
  getMileage,
  getVehicleProperty,
  getContactInfo,
  getAppointmentParams,
  getAdvisorId,
  getParticipantComments,
  getCallBack,
  getPrivacySettings,
  getTransportationOption,
  getServices,
  getConfirmationKey,
  getActivePromoApptData,
  isBookApptInProgress
} from 'store/reducers/appointment/selectors';
import { getTokenId } from 'store/reducers/tokenId/selectors';
import {
  getAppFlow,
  getAppName
} from 'store/reducers/userInteractions/selectors';
import { getIsDeeplinking } from 'store/reducers/deeplink/selectors';
import { getTranslate } from 'store/reducers/localizationString/selectors';
import { selectors as socialSelectors } from 'store/reducers/user/social/';
import { getValetObjectDetails } from 'store/reducers/appointment/valetDetails/selectors';
import {
  getVehicles,
  getPersonInfo,
  getPersonId,
  getPrivacyPreferences
} from 'store/reducers/user/personInfo/selectors';
import {
  getIsObfuscated,
  getIsMobileObfuscated
} from 'store/reducers/appointment/contactInfo/selectors';
import { getOperationItemId } from 'store/reducers/declined/selectors';
import {
  getIsQuoteAppt,
  getQuoteConfirmationId,
  getFormattedQuotePackage,
  getFormattedQuoteServices
} from 'store/reducers/quoteAppt/selectors';

// Constants
import { SERVICES_ROUTE } from 'constants/routes';
import {
  VEHICLE_PROPERTIES,
  VEHICLE_VALUES,
  VEHICLE_TRIM
} from 'constants/vehicle';
import { SEARCH_CRITERIA_FIND_ME } from 'constants/findMe';
import { CHANNEL, WHOAMI } from 'constants/appConfig';
import { NAVIGATION } from 'constants/navigations';
import { getService } from 'constants/ajax';
import { SERVICES_KIND } from 'constants/services';
import { APP_FLOW, CANCELLED } from 'constants/appFlow';
import { TRANSPORTATION } from 'constants/transportation';
import { GA_EVENTS } from 'constants/ga';
import { CLICKTRACKING_TRIGGERS } from 'constants/clickTracking';
import { APP_NAMES } from 'constants/appName';
import { NEW_APPT } from 'constants/deeplink';
import { C8_TOGGLE_PRIVACY_SETTINGS_VALUES } from 'constants/privacySettings';

// Utils
import { isEmpty, formatPhoneNumber } from 'utils/string';
import { isTrue, isObjEmpty } from 'utils/varValidation';
import facebook from 'utils/oauth/FacebookOAuth';
import google from 'utils/oauth/GoogleOAuth';
import { customerVehicleShape } from 'utils/shape/customerVehicle';
import format from 'date-fns/format';
import { getDrivingCondition } from 'store/reducers/trims/selectors';
import { clearTime } from 'utils/date';

const { MAKE, YEAR, MODEL, MILEAGE, REG_DATE } = VEHICLE_PROPERTIES;
const {
  TRIM,
  TRANSMISSION,
  DRIVE_TYPE,
  ENGINE_TYPE,
  ENGINE_SIZE
} = VEHICLE_TRIM;
const { PHONE, EMAIL, LICENSE_NUMBER } = SEARCH_CRITERIA_FIND_ME;
const { FORWARD } = NAVIGATION;
const { IM_NEW_HERE, LOG_ME_IN, QUICK_APPT } = APP_FLOW;
const { VALET, VALETLOANER, DROPOFF } = TRANSPORTATION;

export const resetApptState = () => ({
  type: actionTypes.RESET_APPT
});

export const constructBookServiceData = params => async (
  dispatch,
  getState
) => {
  const personInfo = getPersonInfo(getState());
  const teamIds = getTeamIds(getState());
  const startTime = getStartTime(getState());
  const transportationOption = getTransportationOption(getState());
  const selectedDate = format(startTime, 'MM/DD/YYYY');
  const selectedTime = format(startTime, 'hh:mm a');
  const {
    metaVehicleId,
    vehicleId: vehicleIdFromState,
    displayTrim,
    displayEngineType,
    displayEngineSize,
    displayDriveType,
    displayTransmissionType,
    personId: customerPersonId,
    isRegDateEditable,
    userRegDt,
    roHistoryCnt,
    mileage,
    mileageDt,
    transmission,
    metaVehicleDetailId,
    lastDrivingCondition,
    preferredAdvisorId,
    regDate,
    ...vehicle
  } = getVehicle(getState());
  const isObfuscated = getIsObfuscated(getState());

  // prettier-ignore
  const { phone: phoneNumber, firstName, lastName, email, mobilePhone } = getContactInfo(getState());

  const notes = getParticipantComments(getState());
  const callbackRequired = getCallBack(getState()) ? 1 : 0;
  const ignoreConflicts = true;
  const channel = CHANNEL;
  const apptFlow = getAppFlow(getState());
  const whoami = WHOAMI;
  const {
    make,
    model,
    reservationId,
    services,
    ...apptParams
  } = getAppointmentParams(getState());
  const repairMaintenanceService = getServices(getState())
    .filter(service => service.serviceKind !== SERVICES_KIND.AUTO_RECALL)
    .map(
      ({ serviceKind, discountedPrice, promotion, priceCaption, ...service }) =>
        service
    );
  const { emailPrivacySetting, textPrivacySetting } = getPrivacySettings(
    getState()
  );
  const isEmailOptionEnabled = isTrue(
    getTranslate(getState())('c8.appointment.option.email')
  );
  const isSmsOptionEnabled = isTrue(
    getTranslate(getState())('c8.appointment.option.sms')
  );

  let vehicleId;
  const vehicleIdFromQP = getQueryParam('vehicleId')(getState());
  const textEnabled =
    textPrivacySetting === C8_TOGGLE_PRIVACY_SETTINGS_VALUES.TEXT_ENABLED;
  if (apptFlow !== IM_NEW_HERE && vehicleIdFromState) {
    vehicleId = vehicleIdFromState;
  }

  if (!vehicleId && reservationId && vehicleIdFromQP) {
    vehicleId = vehicleIdFromQP;
  }

  // @TODO: refactor this
  if (textEnabled && apptFlow === LOG_ME_IN && personInfo) {
    const mobilePhoneNumber = {
      contactInfo: {
        phoneNumbers: [
          {
            type: 'HOME',
            digits: phoneNumber
          },
          {
            type: 'MOBILE',
            digits: mobilePhone
          }
        ]
      }
    };
    await dispatch(updateProfile(mobilePhoneNumber));
  }
  const isMobileObfuscated = getIsMobileObfuscated(getState());
  const valetDetails = getValetObjectDetails(getState());
  const promoData = getActivePromoApptData(getState());
  const drivingCondition = getDrivingCondition(getState());

  const sendRecaptchaToken = reservationId
    ? isRecaptchaEnabledOnRescheduleAppt(getState())
    : isRecaptchaEnabledOnConfirmAppt(getState());
  const gRecaptchaResponse = getRecaptchaToken(getState());

  const isQuoteAppt = getIsQuoteAppt(getState());
  const quoteApptData = isQuoteAppt
    ? {
        quoteConfirmationCode: getQuoteConfirmationId(getState()),
        quoteServicePoint: JSON.stringify(getFormattedQuotePackage(getState())),
        quoteServices: JSON.stringify(getFormattedQuoteServices(getState()))
      }
    : {};

  // Save mobile phone on user interaction
  const mobilePhoneNumber =
    textEnabled && !isMobileObfuscated ? mobilePhone : null;
  if (mobilePhoneNumber) {
    dispatch(setSavedMobile(mobilePhoneNumber));
  }

  const bookServiceData = {
    ...params,
    ...apptParams,
    ...(reservationId && { reservationId }),
    services: JSON.stringify(repairMaintenanceService),
    ...(isEmailOptionEnabled && { emailPrivacySetting }),
    ...(isSmsOptionEnabled && { textPrivacySetting }),
    mobilePhoneNumber,
    transportationOption,
    advisorId:
      isValetOrLoanerIntegration(getState()) & !teamIds
        ? ''
        : getAdvisorId(getState()),
    ...(teamIds && { teamIds }),
    selectedDate,
    selectedTime,
    vehicle: JSON.stringify({
      ...vehicle,
      drivingCondition,
      mileage,
      vehRegDate: formatDate(
        regDate ? regDate : new Date(vehicle.year, 0, 1),
        'MM/DD/YYYY'
      ),
      ...(!isObfuscated && { phoneNumber }),
      transmissionType: transmission
    }),
    mileage,
    ...(!isObfuscated && {
      firstName,
      lastName,
      email,
      phoneNumber: reservationId ? phoneNumber : ''
    }),
    ...(isObfuscated && { customerPersonId }),
    notes,
    callbackRequired,
    ignoreConflicts,
    channel,
    apptFlow,
    whoami,
    vehicleId,
    extTransId: getQueryParam('extId')(getState()),
    extTransIdCtxt: getQueryParam('extctxt')(getState()),
    ...promoData,
    ...valetDetails,
    externalValetProvider: isValetOrLoanerIntegration(getState())
      ? isValetOrLoanerIntegration(getState())
      : '',
    ...(sendRecaptchaToken && { gRecaptchaResponse }),
    ...quoteApptData
  };

  // DE36442: don't send vehicle id if the user came through find me
  if (apptFlow === APP_FLOW.FIND_ME && !reservationId && !isObfuscated) {
    // vehicleId is a required field for /reschedule... service
    delete bookServiceData.vehicleId;
  }

  // if the user came through a social provider send an additional node
  if (socialSelectors.isPartiallySignedIn(getState())) {
    const providerMap = {
      facebook,
      google
    };
    const selectedProvider =
      providerMap[socialSelectors.getPartialSignInProvider(getState())];
    invariant(selectedProvider, 'provider unknown');
    bookServiceData.socialLogin = JSON.stringify({
      provider: selectedProvider.getName(),
      socialProviderUserTokenId: await selectedProvider.getToken()
    });
  }

  return bookServiceData;
};

export const bookAppt = params => async (dispatch, getState) => {
  if (isBookApptInProgress(getState())) {
    /* eslint-disable no-console */
    console.log(
      'Book Appointment request is already in progress. New Book Appointment request blocked'
    );
    /* eslint-enable no-console */
    return null;
  }
  const { reservationId } = getAppointmentParams(getState());
  const apptFlow = getAppFlow(getState());
  const { emailPrivacySetting, textPrivacySetting } = getPrivacySettings(
    getState()
  );

  const bookService = getService(reservationId ? 'reschedule' : 'confirm');
  const webkey = getWebkey(getState());
  bookService.setPathVars({ webkey });
  bookService.setCallBacks(dispatch(ajaxCallbacks()));
  const bookServiceData = await dispatch(constructBookServiceData(params));

  const request = xws(bookService).post(
    bookService.getPath(),
    qs.stringify(bookServiceData)
  );

  try {
    const {
      data: { success, appointment }
    } = await request;

    // @TODO (Miguel): there is a bug that /recommended (packages) is called twice first one with wrong mileage
    // @TODO that means that collecting data for shift was breaking the app trying to access a property of undefined
    // Setting ConfirmationKey in GA dimension
    dispatch(gtmSetDimensionConfirmationKey(appointment.confirmationKey));
    // @TODO (Miguel): refactor conversion events, please!
    if (!reservationId) {
      // @GA CONFIRM EVENT
      dispatch(gaClickTrackingEvent(GA_EVENTS.CONFIRM));

      // @CLICKTRACKING CONFIRM EVENT
      dispatch(
        clickTrackingEvent(
          CLICKTRACKING_TRIGGERS.CONFIRM,
          appointment.confirmationKey
        )
      );

      // @MILEONE CONVERSANT
      if (getMileOne(getState())) {
        try {
          const myimageMileOne = new Image();
          myimageMileOne.src =
            'https://t.myvisualiq.net/activity_pixel?pt=i&et=a&r=8888&ago=212&ao=811&px=430&ord=[order_id]&revenue=[revenue]';
        } catch (e) {
          /* eslint-disable no-console */
          console.log(`MileOne event error ${e}`);
          /* eslint-enable no-console */
        }
      }

      // @SHIFT APPT SET EVENT
      dispatch(triggerShiftEvent(getShiftServiceApptSetObj(getState())));

      // @GTM CONVERSANT APPT
      dispatch(
        gtmGenericEvent(
          'Appointment Conversion',
          'Conversion',
          'Create Appointment'
        )
      );

      // @GTM Third Party Valet Conversion
      dispatch(
        gtmThirdPartyValetConvertionEvent(appointment.transportationOption)
      );
    } else {
      dispatch(
        gtmGenericEvent(
          'Appointment Conversion',
          'Conversion',
          'Modify Appointment'
        )
      );
    }
    dispatch(gtmSetDimensionConfirmationKey(undefined));

    dispatch(resetQuoteAppt());
    await dispatch(setApptDetails(appointment));
    const tempVehicle = appointment.vehicle;
    const vehiclesList = getVehicles(getState());
    tempVehicle.appointmentList = [];
    if (vehiclesList) {
      let newAppt = false;
      vehiclesList.forEach(vehicle => {
        if (vehicle.vehicleId === appointment.vehicle.vehicleId) {
          const existAppt = vehicle.appointmentList.findIndex(
            v => v.reservationId === appointment.reservationId
          );
          newAppt = true;
          if (existAppt >= 0) {
            vehicle.appointmentList.forEach((v, index) => {
              if (index === existAppt) {
                tempVehicle.appointmentList.push(
                  setAppointmentList(appointment, v)
                );
              } else {
                tempVehicle.appointmentList.push(v);
              }
            });
            return dispatch(updateUserVehicles(tempVehicle));
          } else {
            return dispatch(
              updateUserVehicles({
                ...appointment.vehicle,
                appointmentList: [setAppointmentList(appointment)]
              })
            );
          }
        }
      });
      if (!newAppt) {
        dispatch(
          updateUserVehicles({
            ...appointment.vehicle,
            appointmentList: [setAppointmentList(appointment)]
          })
        );
      }
    }

    if (apptFlow === LOG_ME_IN) {
      dispatch(
        updatePrivacySettings({ emailPrivacySetting, textPrivacySetting })
      );
    }
    /* eslint-disable no-console */
    console.log(appointment);
    /* eslint-enable no-console */
    return success;
  } catch (e) {
    /* eslint-disable no-console */
    console.log(e);
    return false;
    /* eslint-enable no-console */
  }
};

const setAppointmentList = (appointment, vehicleInfo?) => {
  return {
    ...(vehicleInfo && { vehicleInfo }),
    reservationId: appointment.reservationId,
    startTime: appointment.startTime,
    confirmationKey: appointment.confirmationKey,
    attendanceStatus: appointment.attendanceStatus
  };
};

export const cancelAppt = () => async (dispatch, getState) => {
  const webkey = getWebkey(getState());
  const { reservationId } = getAppointmentParams(getState());
  const apptFlow = isEmpty(getAppFlow(getState()))
    ? IM_NEW_HERE
    : getAppFlow(getState());
  const channel = CHANNEL;
  const cancelService = getService('cancel');
  const gRecaptchaResponse = getRecaptchaToken(getState());
  const sendRecaptchaToken = isRecaptchaEnabledOnCancelAppt(getState());

  cancelService.setPathVars({ webkey, reservationId });
  cancelService.setCallBacks(dispatch(ajaxCallbacks()));
  try {
    const {
      data: { success }
    } = await xws(cancelService).post(
      cancelService.getPath(),
      qs.stringify({
        tokenId: getTokenId(getState()),
        locale: getLocaleCode(getState()),
        channel,
        apptFlow,
        ...(sendRecaptchaToken && { gRecaptchaResponse })
      })
    );
    dispatch(gtmSetDimensionConfirmationKey(getConfirmationKey(getState())));
    dispatch(
      gtmGenericEvent(
        'Appointment Conversion',
        'Conversion',
        'Cancel Appointment'
      )
    );
    dispatch(gtmSetDimensionConfirmationKey(undefined));

    // @GA EVENT CANCEL
    dispatch(gaClickTrackingEvent(GA_EVENTS.CANCEL));
    dispatch(clickTrackingEvent(CLICKTRACKING_TRIGGERS.CANCEL));

    return success;
  } catch (e) {
    /* eslint-disable no-console */
    console.log(e);
    /* eslint-enable no-console */
    return false;
  }
};

export const makeApptDetailsCall = config => async (dispatch, getState) => {
  const webkey = getWebkey(getState());
  const appointmentDetailsService = getService('details');
  appointmentDetailsService.setPathVars({ webkey });
  appointmentDetailsService.setCallBacks(dispatch(ajaxCallbacks()));
  try {
    const {
      data: { appointment }
    } = await xws(appointmentDetailsService).get(
      appointmentDetailsService.getPath(),
      {
        params: {
          ...config,
          locale: getLocaleCode(getState()),
          tokenId: getTokenId(getState())
        }
      }
    );
    return appointment;
  } catch (e) {
    /* eslint-disable no-console */
    console.log(e);
    /* eslint-enable no-console */
    return false;
  }
};

// TODO: move getSelfCheckInAppointment when redux node is created
export const getSelfCheckInAppointment = config => async (
  dispatch,
  getState
) => {
  const appointment = await dispatch(makeApptDetailsCall(config));
  const { OTHER } = VEHICLE_VALUES;
  const contactInfo = appointment.personInfo;
  const startTimeFormatted = parseDate(`${appointment.startTime}T00:00:00`);
  const { id: personId } = contactInfo;
  await dispatch(actions.setPersonInfo(contactInfo));
  const { userRegDate, ...vehicleData } = appointment.vehicle;
  const regDate = parseDate(`${userRegDate}T00:00:00`);
  const vehicle = !isNaN(new Date(regDate).getTime())
    ? {
        vehRegDate: formatDate(regDate, 'MM/DD/YYYY'),
        regDate: userRegDate,
        ...vehicleData,
        personId
      }
    : { ...vehicleData, personId };
  const existMake = findMake(getState())(vehicle.make);
  if (!existMake) {
    dispatch(setMake(OTHER));
  }
  const shapedVehicle = existMake
    ? customerVehicleShape(vehicle)
    : customerVehicleShape({
        ...vehicle,
        make: OTHER,
        model: OTHER
      });
  dispatch({
    type: actionTypes.SET_CUSTOMER_VEHICLE,
    payload: shapedVehicle
  });
  const services = appointment.maintenanceServices.concat(
    appointment.repairServices.concat(appointment.autoRecallServices)
  );
  const formattedServices = dispatch(formatServices(services));
  dispatch(setServices(formattedServices));
  dispatch(setApptServicePoint(appointment.servicePoint));
  await dispatch(updatePackageInfo());
  const promoList = appointment.promoId
    ? await dispatch(getPromoList(vehicleData.vin, appointment.promoId))
    : [];
  if (promoList && promoList[0]) {
    dispatch(setActivePromotion(promoList[0]));
  }
  dispatch(setDeclinedServices(appointment.declinedServices));
  dispatch(setAutoRecallServices(appointment.autoRecallServices));
  dispatch(setAdvisor(appointment.agentId));
  dispatch(setAdvisorName(appointment.agentName));
  dispatch(setStartTime(startTimeFormatted));
  dispatch(setTransportationOption(appointment.transportationOption));
  if (
    appointment.valetDetails &&
    (appointment.transportationOption === VALET ||
      appointment.transportationOption === VALETLOANER)
  ) {
    dispatch(setValetDetails(appointment.valetDetails));
  }
  dispatch(setReservationId(appointment.reservationId));
  dispatch(setAttendanceStatus(appointment.attendanceStatus));
  dispatch(setConfirmationKey(appointment.confirmationKey));
  dispatch(setCallBack(appointment.callBack));
  dispatch(setLifecycleState(appointment.lifecycleState));
  if (appointment.lead) {
    dispatch(setLeadAppt(appointment.lead));
  }
  return appointment;
};

export const getAppointmentDetails = config => async (dispatch, getState) => {
  const webkey = getWebkey(getState());
  const appointmentDetailsService = getService('details');
  appointmentDetailsService.setPathVars({ webkey });
  appointmentDetailsService.setCallBacks(dispatch(ajaxCallbacks()));
  try {
    const {
      data: { appointment }
    } = await xws(appointmentDetailsService).get(
      appointmentDetailsService.getPath(),
      {
        params: {
          ...config,
          locale: getLocaleCode(getState()),
          tokenId: getTokenId(getState())
        }
      }
    );
    if (appointment.personInfo) {
      await dispatch(setExtId(appointment.personInfo.extId));
    }
    await dispatch(setApptDetails(appointment));
    await dispatch(updatePackageInfo());
    await dispatch(getTransportationOptions(true));
    return appointment;
  } catch (e) {
    /* eslint-disable no-console */
    console.log(e);
    /* eslint-enable no-console */
    return false;
  }
};

export const setLeadAppt = lead => {
  return {
    type: actionTypes.SET_APPT_LEAD,
    payload: lead
  };
};

export const setLifecycleState = lifecycleState => {
  return {
    type: actionTypes.SET_LIFECYCLE_STATE,
    payload: lifecycleState
  };
};

export const formatServices = services => (dispatch, getState) => {
  const getTellusmoreSvcId = getPolicyProperty(getState());
  return services.map(service => {
    const {
      dmsOpcode,
      serviceIds: id,
      serviceComments: notes,
      price,
      originalPrice,
      shopDuration,
      serviceKind,
      quoteServiceId
    } = service;
    let { name } = service;
    if (id && id === getTellusmoreSvcId('tellusmoreSvcId')) {
      name = notes;
    }
    return {
      dmsOpcode,
      id: +id,
      name,
      price: +price || +originalPrice,
      shopDuration,
      notes,
      serviceKind,
      quoteServiceId
    };
  });
};

/**
 * `appointment` is the response received from
 * xws/rest/dealer/xtimemotors/appointment/details
 */
const setApptDetails = appointment => async (dispatch, getState) => {
  const contactInfo = appointment.personInfo;
  const { id: personId } = contactInfo;
  const {
    avgMileage,
    diveTypeLabel,
    engineSizeLabel,
    engineTypeLabel,
    isDefault,
    mileageDate,
    modelLabel,
    transmissionTypeLabel,
    trimLabel,
    userRegDate,
    nextMenuMileage,
    notifications,
    nextMenuName,
    ...vehicleData
  } = appointment.vehicle;
  const regDate = parseDate(`${userRegDate}T00:00:00`);
  const startTimeFormatted = parseDate(`${appointment.startTime}T00:00:00`);
  const endTimeFormatted = parseDate(`${appointment.endTime}T00:00:00`);
  const services = appointment.maintenanceServices.concat(
    appointment.repairServices,
    appointment.globalServices
  );
  const formattedServices = dispatch(formatServices(services));
  const vehicle = !isNaN(new Date(regDate).getTime())
    ? {
        vehRegDate: formatDate(regDate, 'MM/DD/YYYY'),
        regDate: userRegDate,
        ...vehicleData,
        personId
      }
    : { ...vehicleData, personId };
  const apptMileage =
    appointment.servicePoint && appointment.servicePoint.mileage;
  await dispatch(setCustomerVehicle(vehicle));
  dispatch(setPersonId(personId));

  const promoId = appointment.promoId;
  let promoList = [];
  if (promoId) {
    promoList = await dispatch(
      getPromoList(vehicleData.vin, appointment.promoId)
    );
    if (promoList && promoList[0]) {
      dispatch(setActivePromotion(promoList[0]));
    } else {
      dispatch(getAnonymousPromotion(appointment.promoId));
    }
  }

  dispatch(setParticipantComments(appointment.participantComments));
  dispatch(setApptServicePoint(appointment.servicePoint));
  if (apptMileage && vehicle.mileage !== apptMileage) {
    dispatch(clearPackages());
    await dispatch(getPackages(apptMileage));
  }

  /**
   * Reschedule appointment if contains Quote services
   */
  if (appointment.quoteConfirmationCode) {
    const allServices = services.concat(appointment.declinedServices);
    dispatch(
      setQuoteAppointmentData(appointment, allServices, formattedServices)
    );
  } else {
    dispatch(setDeclinedServices(appointment.declinedServices));
  }

  dispatch(
    setServices(formattedServices.concat(appointment.autoRecallServices))
  );
  dispatch(setAutoRecallServices(appointment.autoRecallServices));
  dispatch(setAdvisor(appointment.agentId));
  dispatch(setAdvisorName(appointment.agentName));
  appointment.teamId && dispatch(setTeamIds(appointment.teamId));
  const curDateClearTime = clearTime(new Date());
  const apptDateClearTime = clearTime(new Date(parseDate(startTimeFormatted)));
  const scope = getQueryParam('scope')(getState());
  if (apptDateClearTime >= curDateClearTime || scope !== NEW_APPT) {
    dispatch(setStartTime(startTimeFormatted));
  }

  dispatch(setTransportationOption(appointment.transportationOption));
  if (
    appointment.valetDetails &&
    (appointment.transportationOption === VALET ||
      appointment.transportationOption === VALETLOANER)
  ) {
    dispatch(setValetDetails(appointment.valetDetails));
  }
  if (appointment.lifecycleState !== CANCELLED) {
    dispatch(setEndTime(endTimeFormatted));
    dispatch(setReservationId(appointment.reservationId));
    dispatch(setAttendanceStatus(appointment.attendanceStatus));
    dispatch(setConfirmationKey(appointment.confirmationKey));
    dispatch(setLifecycleState(appointment.lifecycleState));
  }

  dispatch(setCallBack(appointment.callBack));
  dispatch(setIsCarryOver(appointment.isCarryover));
  dispatch(setIsDmsAppt(appointment.isDmsAppt));
  dispatch(
    setIsQuoteAppt(
      appointment.isQuoteAppt || !!appointment.quoteConfirmationCode
    )
  );
  if (appointment.lead) {
    dispatch(setLeadAppt(appointment.lead));
  }
  dispatch(modifyEnabled());
  dispatch(apptNotModified());
};

export const doReschedule = params => async (dispatch, getState) => {
  const doRescheduleService = getService('reschedule');
  const webkey = getWebkey(getState());
  doRescheduleService.setPathVars({ webkey });
  doRescheduleService.setCallBacks(dispatch(ajaxCallbacks()));
  const {
    // TODO: remove this check when this is refactored
    // eslint-disable-next-line no-unused-vars
    data: { appointment }
  } = await xws(doRescheduleService).post(
    doRescheduleService.getPath(),
    qs.stringify({ ...params, ...dealerLocaleSelector(getState()) })
  );
  // appointment && dispatch(setAppointmentDetails(appointment)); // @TODO refactor
};

export const setMetaVehicleId = metaVehicleId => {
  return {
    type: actionTypes.SET_META_VEHICLE_ID,
    payload: metaVehicleId
  };
};

export const getMetaVehicleId = ({ make, year, model, ...vehicle }) => async (
  dispatch,
  getState
) => {
  const metaVehicleIdService = getService('metaVehicle');
  const webkey = getWebkey(getState());
  const { OTHER } = VEHICLE_VALUES;

  const { transmission, transmissionType } = vehicle;

  let vehicleTemp;
  if (make === OTHER && model === OTHER) {
    vehicleTemp = { make, year, model };
  } else {
    vehicleTemp = {
      make,
      year,
      model,
      transmissionType: transmissionType || transmission,
      ...vehicle
    };
    delete vehicleTemp.transmission;
  }

  metaVehicleIdService.setPathVars({ webkey });
  metaVehicleIdService.setCallBacks(dispatch(ajaxCallbacks()));
  try {
    const {
      data: { data }
    } = await xws(metaVehicleIdService).post(
      metaVehicleIdService.getPath(),
      qs.stringify(vehicleTemp)
    );
    await dispatch(setMetaVehicleId(data.id));
    return data;
  } catch (e) {
    throw Error(e);
  }
};

export function setAppointmentDetails(appointment) {
  return {
    type: actionTypes.SET_APPOINTMENT,
    payload: appointment
  };
}

// comments node
export function setParticipantComments(participantComments) {
  return {
    type: actionTypes.SET_PARTICIPANT_COMMENTS,
    payload: participantComments
  };
}

// callBack node
export function setCallBack(callBack) {
  return {
    type: actionTypes.SET_CALL_BACK,
    payload: callBack
  };
}

// appointment node
export function setTeamIds(teamIds) {
  return {
    type: actionTypes.SET_TEAM_IDS,
    payload: teamIds
  };
}

export function setStartTime(startTime) {
  return {
    type: actionTypes.SET_START_TIME,
    payload: startTime
  };
}

export function setEndTime(endTime) {
  return {
    type: actionTypes.SET_END_TIME,
    payload: endTime
  };
}

export function setReservationId(reservationId) {
  return {
    type: actionTypes.SET_RESERVATION_ID,
    payload: reservationId
  };
}

export function setTransportationOption(transportationOption) {
  return {
    type: actionTypes.SET_TRANSPORTATION_OPTION,
    payload: transportationOption
  };
}

export function setTransportationLabel(transportationDisplayText) {
  return {
    type: actionTypes.SET_TRANSPORTATION_LABEL,
    payload: transportationDisplayText
  };
}

export const setTransportationInfo = transpOptionInfo => {
  return {
    type: actionTypes.SET_TRANSP_OPTION_INFO,
    payload: transpOptionInfo
  };
};

export function setAttendanceStatus(attendanceStatus) {
  return {
    type: actionTypes.SET_ATTENDANCE_STATUS,
    payload: attendanceStatus
  };
}

export function setConfirmationKey(confirmationKey) {
  return {
    type: actionTypes.SET_CONFIRMATION_KEY,
    payload: confirmationKey
  };
}

export function setIsCarryOver(isCarryOver) {
  return {
    type: actionTypes.SET_CARRY_OVER,
    payload: isCarryOver
  };
}

export function setIsDmsAppt(isDmsAppt) {
  return {
    type: actionTypes.SET_DMS_APPT,
    payload: isDmsAppt
  };
}

export function setIsQuoteAppt(isQuoteAppt) {
  return {
    type: actionTypes.SET_IS_QUOTE_APPT,
    payload: isQuoteAppt
  };
}

// vehicle node

export const selectMake = (itemSelected, awaitNeeded) => async (
  dispatch,
  getState
) => {
  const selectedMake = getVehicleProperty(getState())('make');
  if (itemSelected !== selectedMake) {
    dispatch(restartVehicleFrom(MAKE));
  }
  dispatch(setMake(itemSelected));
  return awaitNeeded
    ? await dispatch(getYears(itemSelected))
    : dispatch(getYears(itemSelected));
};

export const selectYear = itemSelected => async (dispatch, getState) => {
  const selectedYear = getVehicleProperty(getState())('year');
  if (itemSelected !== selectedYear) {
    dispatch(restartVehicleFrom(YEAR));
  }
  dispatch(setYear(itemSelected));
  return await dispatch(getModels(itemSelected));
};

export const selectModel = (itemSelected, intl) => async (
  dispatch,
  getState
) => {
  const selectedModel = getVehicleProperty(getState())('model');
  const appFlow = getAppFlow(getState());

  if (selectedModel !== itemSelected) {
    dispatch(restartVehicleFrom(MODEL));
  }
  dispatch(setModel(itemSelected));
  if (appFlow !== QUICK_APPT) {
    return await dispatch(getTrims(intl));
  }
};

export const selectTransmission = itemSelected => async (
  dispatch,
  getState
) => {
  const selectedTransmission = getVehicleProperty(getState())('transmission');
  if (selectedTransmission !== itemSelected) {
    dispatch(restartVehicleFrom(TRANSMISSION));
  }
  dispatch(setTransmission(itemSelected));
};

export const selectDriveType = itemSelected => async (dispatch, getState) => {
  const selectedDriveType = getVehicleProperty(getState())('driveType');
  if (selectedDriveType !== itemSelected) {
    dispatch(restartVehicleFrom(DRIVE_TYPE));
  }
  dispatch(setDriveType(itemSelected));
};

export const selectTrim = itemSelected => async (dispatch, getState) => {
  const selectedTrim = getVehicleProperty(getState())('trim');
  if (selectedTrim !== itemSelected) {
    dispatch(restartVehicleFrom(TRIM));
  }
  dispatch(setTrim(itemSelected));
};

export const selectEngineType = itemSelected => (dispatch, getState) => {
  const selectedEngineType = getVehicleProperty(getState())('engineType');
  if (selectedEngineType !== itemSelected) {
    dispatch(restartVehicleFrom(ENGINE_TYPE));
  }
  dispatch(setEngineType(itemSelected));
};

export const selectEngineSize = itemSelected => async (dispatch, getState) => {
  const selectedEngineSize = getVehicleProperty(getState())('engineSize');
  if (selectedEngineSize !== itemSelected) {
    dispatch(restartVehicleFrom(ENGINE_SIZE));
  }
  dispatch(setEngineSize(itemSelected));
};

export const validateTrimData = () => (dispatch, getState) => {
  const { trim, transmission, driveType, engineType, engineSize } = getVehicle(
    getState()
  );
  if (!trim || !transmission || !driveType || !engineType || !engineSize) {
    dispatch(setIdkSelected(true));
    dispatch(setTrim(null));
    dispatch(setTransmission(null));
    dispatch(setDriveType(null));
    dispatch(setEngineType(null));
    dispatch(setEngineSize(null));
  }
};

export const selectRegDate = itemSelected => async (dispatch, getState) => {
  const selectedRegDate = getVehicleProperty(getState())('regDate');
  if (selectedRegDate !== itemSelected) {
    dispatch(restartVehicleFrom(REG_DATE));
  }
  dispatch(setRegDate(itemSelected));
};

export const selectMileage = (mileage, delay) => async dispatch => {
  dispatch(setMileage(mileage));
  const timeoutDelay = delay ? 1000 : 0;
  setTimeout(() => {
    dispatch(goTo(SERVICES_ROUTE, FORWARD));
    dispatch(loadServices());
  }, timeoutDelay);
};

export const restartVehicleFrom = from => dispatch => {
  dispatch(clearServices());
  if (from === MILEAGE || from === ENGINE_SIZE || from === REG_DATE) {
    return;
  }
  dispatch(setEngineSize(null));
  if (from === ENGINE_TYPE) {
    return;
  }
  dispatch(setEngineType(null));
  if (from === TRIM) {
    return;
  }
  dispatch(setTrim(null));
  if (from === DRIVE_TYPE) {
    return;
  }
  dispatch(setDriveType(null));
  if (from === TRANSMISSION) {
    return;
  }
  dispatch(setTransmission(null));
  dispatch(setTrims({}));
  if (from === MODEL) {
    return;
  }
  dispatch(setModels([]));
  dispatch(setModel(null));
  if (from === YEAR) {
    return;
  }
  dispatch(setYears([]));
  dispatch(setYear(null));
};

export function setMake(make) {
  return {
    type: actionTypes.SET_MAKE,
    payload: make
  };
}
export function setYear(year) {
  return {
    type: actionTypes.SET_YEAR,
    payload: year
  };
}
export function setModel(model) {
  return {
    type: actionTypes.SET_MODEL,
    payload: model
  };
}

export function setTrim(trim) {
  return {
    type: actionTypes.SET_TRIM,
    payload: trim
  };
}

export function setTransmission(transmission) {
  return {
    type: actionTypes.SET_TRANSMISSION,
    payload: transmission
  };
}

export function setDriveType(driveType) {
  return {
    type: actionTypes.SET_DRIVE_TYPE,
    payload: driveType
  };
}

export function setEngineType(engineType) {
  return {
    type: actionTypes.SET_ENGINE_TYPE,
    payload: engineType
  };
}

export function setEngineSize(engineSize) {
  return {
    type: actionTypes.SET_ENGINE_SIZE,
    payload: engineSize
  };
}

export function setRegDate(regDate) {
  return {
    type: actionTypes.SET_REG_DATE,
    payload: regDate
  };
}

export function setLicenseNumber(licenseNumber) {
  return {
    type: actionTypes.SET_LICENSE_NUMBER,
    payload: licenseNumber
  };
}

export const setMileage = (payload, restartVehicle = true) => async (
  dispatch,
  getState
) => {
  const selectedMileage = getMileage(getState());
  let mileage;
  dispatch(setExactMileageFlag(0));
  if (+payload !== +selectedMileage && restartVehicle) {
    dispatch(restartVehicleFrom(MILEAGE));
  }
  if (payload === undefined || !+payload) {
    mileage = null;
  } else {
    mileage = +payload;
  }
  dispatch({
    type: actionTypes.SET_MILEAGE,
    payload: mileage
  });
};

// maintenance services
export const onAddRemoveServices = () => async (dispatch, getState) => {
  dispatch({
    type: actionTypes.CLEAR_AVAILABLE_TIMES
  });
  dispatch({
    type: actionTypes.CLEAR_START_TIME
  });
  dispatch({
    type: actionTypes.CLEAR_APPOINTMENT_SELECTED_DAY
  });
  dispatch({
    type: actionTypes.CLEAR_APPT_OPTIONS
  });
};

export const selectQuickApptOptions = (tellusMoreText: string) => async (
  dispatch,
  getState
) => {
  dispatch(setAdvisor(null)); // set 'any advisor'
  dispatch(setTransportationOption(DROPOFF));
  const tellusMoreScdId = getTellusmoreSvcId(getState());
  const tellusMoreService = [
    {
      id: tellusMoreScdId,
      notes: tellusMoreText,
      name: tellusMoreText,
      price: 0
    }
  ];
  dispatch(setServices(tellusMoreService));
};

export const setServices = (services = []) => async dispatch => {
  const formattedServices = services.map(
    ({
      id,
      shopDuration,
      dmsOpcode,
      name,
      price,
      notes,
      serviceKind,
      quoteServiceId
    }) => {
      return {
        id,
        shopDuration,
        notes,
        dmsOpcode,
        name,
        price: price || 0,
        serviceKind,
        quoteServiceId
      };
    }
  );
  dispatch({
    type: actionTypes.SET_SERVICES,
    payload: formattedServices
  });
};

export const setAutoRecallServices = recallServices => dispatch => {
  const formattedServices = recallServices.map(
    ({ id, shopDuration, dmsOpcode, name, price, notes, serviceKind }) => {
      return { id, shopDuration, notes, dmsOpcode, name, price, serviceKind };
    }
  );
  dispatch({
    type: actionTypes.SET_AUTORECALL_SERVICES,
    payload: formattedServices
  });
};

export const addService = ({
  id,
  shopDuration,
  dmsOpcode,
  name,
  price,
  discountedPrice,
  serviceKind,
  notes,
  priceCaption,
  quoteServiceId
}) => async (dispatch, getState) => {
  const apptServices = getServices(getState());
  const isGlobalRepair = serviceKind === 'global_repair';
  if (
    !apptServices.find(service => {
      if (!isGlobalRepair) {
        return service.id === id;
      } else {
        return service.quoteServiceId === quoteServiceId;
      }
    })
  ) {
    dispatch(onAddRemoveServices());
    dispatch({
      type: actionTypes.ADD_SERVICE,
      payload: {
        ...(!isGlobalRepair && { id }),
        shopDuration,
        dmsOpcode,
        name,
        discountedPrice,
        price,
        notes,
        serviceKind,
        priceCaption,
        quoteServiceId
      }
    });
  }
};

export const removeService = serviceId => async dispatch => {
  dispatch(onAddRemoveServices());
  dispatch({
    type: actionTypes.REMOVE_SERVICE,
    payload: serviceId
  });
};

const getFormattedServicePoint = servicePoint => {
  if (!servicePoint) {
    return null;
  }
  const {
    menuType: { services, ...menuTypeParams },
    ...servicePointParams
  } = servicePoint;
  return {
    ...servicePointParams,
    menuType: {
      ...menuTypeParams,
      totalPrice: menuTypeParams.price || menuTypeParams.totalPrice,
      services: services.map(service => ({
        id: service.id,
        name: service.name,
        notes: service.notes,
        dmsOpcode: service.dmsOpcode,
        waiterAllowed: service.waiterAllowed,
        loanerAllowed: service.loanerAllowed,
        selectable: service.selectable,
        showPrice: service.showPrice || service.selectByDefault,
        discountedPrice: service.discountedPrice,
        price: service.price,
        shopDuration: service.shopDuration
      }))
    }
  };
};

export const updateApptServicePoint = servicePoint => async dispatch => {
  dispatch({
    type: actionTypes.UPDATE_APPT_SERVICE_POINT,
    payload: getFormattedServicePoint(servicePoint)
  });
};

export const setApptServicePoint = servicePoint => async (
  dispatch,
  getState
) => {
  const isSelfCheckIn = getAppName(getState()) === APP_NAMES.SELF_CHECK_IN;
  dispatch(onAddRemoveServices());
  const formattedServicePoint = getFormattedServicePoint(servicePoint);
  if (servicePoint) {
    !isSelfCheckIn && dispatch(gtmUserEventPackageAdded(formattedServicePoint));
    dispatch(removeIncludedServices(formattedServicePoint));
  }
  dispatch({
    type: actionTypes.SET_APPT_SERVICE_POINT,
    payload: formattedServicePoint
  });
};

export const removeIncludedServices = ({ menuType: { services } }) => (
  dispatch,
  getState
) => {
  const selectedServices = getServices(getState());
  services.forEach(service => {
    const alreadySelected = selectedServices.find(
      selectedService => selectedService.id === service.id
    );
    if (alreadySelected) {
      dispatch(removeService(service.id));
    }
  });
};

export const removePackageService = serviceId => async dispatch => {
  dispatch(onAddRemoveServices());
  dispatch({
    type: actionTypes.REMOVE_PACKAGE_SERVICE,
    payload: serviceId
  });
};

export function updateTellusmoreService(service) {
  return {
    type: actionTypes.UPDATE_TELLUSMORE_SERVICE,
    payload: service
  };
}

export function updateServiceComment(id, comment) {
  return {
    type: actionTypes.UPDATE_SERVICE_COMMENT,
    payload: { id, comment }
  };
}

export const addDeclinedService = ({
  id,
  parentPlaceholderId,
  opcode,
  name,
  description,
  duration,
  price,
  operationItemId
}) => async dispatch => {
  dispatch(onAddRemoveServices());
  dispatch({
    type: actionTypes.ADD_DECLINED_SERVICE,
    payload: {
      id,
      parentPlaceholderId,
      duration,
      opcode,
      description,
      name,
      price,
      operationItemId
    }
  });
};

export const formatDeclinedServices = declinedServices => (
  dispatch,
  getState
) => {
  return declinedServices.map(declinedService => {
    const {
      id,
      name,
      duration,
      description,
      parentPlaceholderId,
      definitionId,
      opcode,
      dmsOpcode,
      price,
      originalPrice,
      operationItemId
    } = declinedService;
    const newOperationItemId = getOperationItemId(id)(getState());
    return {
      id,
      parentPlaceholderId: parentPlaceholderId || definitionId,
      opcode: opcode || dmsOpcode,
      name,
      description,
      duration,
      price: +price || +originalPrice,
      operationItemId: operationItemId || newOperationItemId
    };
  });
};

export const setDeclinedServices = (declinedServices = []) => dispatch => {
  const formattedServices = dispatch(formatDeclinedServices(declinedServices));
  dispatch(onAddRemoveServices());
  dispatch({
    type: actionTypes.SET_APPT_DECLINED_SERVICES,
    payload: formattedServices
  });
};

export const removeDeclinedService = service => async dispatch => {
  dispatch(onAddRemoveServices());
  dispatch({
    type: actionTypes.REMOVE_DECLINED_SERVICE,
    payload: service
  });
};

export function clearDeclinedServices() {
  return {
    type: actionTypes.CLEAR_DECLINED_SERVICES
  };
}

export function clearServices() {
  return {
    type: actionTypes.CLEAR_SERVICES
  };
}

export function clearApptServices() {
  return {
    type: actionTypes.CLEAR_APPT_SERVICES
  };
}

// customer CustomerVehicle
// ToDo: verify data in order to not make unnecessary calls
export const setCustomerVehicle = (vehicle, config = {}) => async (
  dispatch,
  getState
) => {
  const { make, year, trim, transmissionType } = vehicle;
  // Deeplink behavior
  const neededServiceAwait =
    getQueryParam('dest')(getState()) === 'APPT' &&
    getIsDeeplinking(getState());
  let vehicleData = customerVehicleShape(vehicle);
  const existMake = findMake(getState())(make);
  if (!existMake) {
    const { OTHER } = VEHICLE_VALUES;
    vehicleData = customerVehicleShape({
      ...vehicle,
      make: OTHER,
      model: OTHER
    });
    dispatch(setMake(OTHER));
    dispatch(setModelLabelOther(vehicle.model));
    dispatch(setMakeLabelOther(vehicle.make));
  }
  dispatch({
    type: actionTypes.SET_CUSTOMER_VEHICLE,
    payload: vehicleData
  });
  const privacyPreferences = getPrivacyPreferences(getState());
  if (!privacyPreferences) {
    const personId = getPersonId(getState());
    if (personId) {
      dispatch(getCustomerPreferences(personId));
    }
  } else {
    dispatch(updatePrivacyPreferences(privacyPreferences));
  }
  dispatch(getYears(vehicleData.make));
  dispatch(getModels(year));
  await dispatch(getTrims(trim));
  dispatch(setTransmission(transmissionType));

  !config.preservServices &&
    (await dispatch({
      type: actionTypes.CLEAR_SERVICES
    }));
  await dispatch(loadServices(neededServiceAwait));
};

export const updatePrivacyPreferences = privacyPreferences => dispatch => {
  const isEmailPrivacyPreferenceOn =
    +privacyPreferences.emailPrivacyPreference[0] > 0;
  const isTextPrivacyPreferenceOn =
    +privacyPreferences.textPrivacyPreference[0] > 0;
  const updatedPrivacySettings = {
    emailPrivacySetting: isEmailPrivacyPreferenceOn
      ? C8_TOGGLE_PRIVACY_SETTINGS_VALUES.EMAIL_ENABLED
      : C8_TOGGLE_PRIVACY_SETTINGS_VALUES.EMAIL_DISABLED,
    textPrivacySetting: isTextPrivacyPreferenceOn
      ? C8_TOGGLE_PRIVACY_SETTINGS_VALUES.TEXT_ENABLED
      : C8_TOGGLE_PRIVACY_SETTINGS_VALUES.TEXT_DISABLED
  };
  dispatch(setPrivacySettings(updatedPrivacySettings));
};

// Status Tracker Method
export const updateReservationStatusPrivacyPreferences = privacyPreferences => dispatch => {
  const isEmailPrivacyPreferenceOn =
    +privacyPreferences.emailPrivacyPreference[12] > 0;
  const isTextPrivacyPreferenceOn =
    +privacyPreferences.textPrivacyPreference[12] > 0;
  const updatedPrivacySettings = {
    emailPrivacySetting: `X,X,X,X,X,X,${isEmailPrivacyPreferenceOn ? 1 : 0}`,
    textPrivacySetting: `X,X,X,X,X,X,${isTextPrivacyPreferenceOn ? 1 : 0}`
  };
  dispatch(setReservationStatusPrivacySettings(updatedPrivacySettings));
};

export const customerVehiclesByField = fieldValue => (dispatch, getState) => {
  const dealerCountryCode = getDealerCountryCode(getState());
  const formattedNumber = formatPhoneNumber(fieldValue, dealerCountryCode);
  if (EMAIL_REGEX.test(fieldValue)) {
    dispatch(setPhoneNumber(''));
    dispatch(setLicenseNumber(''));
    dispatch(setEmail(fieldValue));
    dispatch(setSearchCriteriaFindMe(EMAIL));
    return EMAIL;
  } else if (isValidNumber(formattedNumber, dealerCountryCode)) {
    dispatch(setPhoneNumber(fieldValue));
    dispatch(setLicenseNumber(''));
    dispatch(setEmail(''));
    dispatch(setSearchCriteriaFindMe(PHONE));
    return PHONE;
  } else {
    dispatch(setPhoneNumber(''));
    dispatch(setEmail(''));
    dispatch(setLicenseNumber(fieldValue.toUpperCase()));
    dispatch(setSearchCriteriaFindMe(LICENSE_NUMBER));
    return LICENSE_NUMBER;
  }
};

// Advisor
export const setAdvisor = advisorId => async dispatch => {
  dispatch({
    type: actionTypes.SET_ADVISOR,
    payload: advisorId
  });
};
export function setAdvisorName(advisorName) {
  return {
    type: actionTypes.SET_ADVISOR_NAME,
    payload: advisorName
  };
}

// PrivacySettings
export const setPrivacySettings = settings => {
  return {
    type: actionTypes.SET_PRIVACY_SETTINGS,
    payload: settings
  };
};

export const setReservationStatusPrivacySettings = settings => {
  return {
    type: actionTypes.SET_RESERVATION_STATUS_PRIVACY_SETTINGS,
    payload: settings
  };
};

export const setQuoteAppointmentData = (
  appointment = {},
  services = [],
  formattedServices = []
) => async (dispatch, getState) => {
  dispatch(updateQuoteServices(services, 'maintenance'));
  dispatch(updateQuoteServices(services, 'repair'));
  const { quoteWithDeclinedServices: updatedDeclinedServices } = dispatch(
    updateQuoteServices(services, 'declined')
  );
  dispatch(
    setGlobalRepairServices(
      services.filter(s => s.serviceKind === 'global_repair'),
      'globalRepair'
    )
  );
  dispatch(setDeclinedServices(updatedDeclinedServices));
  const readOnlyServices = formattedServices.concat(updatedDeclinedServices);

  const hasQuoteMenuPackage =
    appointment.servicePoint &&
    !isObjEmpty(appointment.servicePoint) &&
    appointment.servicePoint.quoteServiceId;

  if (hasQuoteMenuPackage) {
    const {
      menuType: { drivingCondition, ...menuType },
      ...servicePointData
    } = appointment.servicePoint;
    const selectedServices =
      menuType.services &&
      menuType.services.map(service => {
        service.selectByDefault = true;
        return service;
      });
    const servicePoint = {
      menuType: {
        ...menuType,
        services: selectedServices
      },
      ...servicePointData,
      selected: true
    };
    dispatch(setQuoteApptServicePoint(servicePoint));
    dispatch(
      setQuoteReadOnlyPackage({
        ...servicePoint.menuType,
        ...servicePointData,
        selected: true
      })
    );
  }

  let allReadOnlyServices = hasQuoteMenuPackage
    ? readOnlyServices.concat(appointment.servicePoint.menuType.services)
    : readOnlyServices;
  allReadOnlyServices =
    allReadOnlyServices &&
    allReadOnlyServices.map(service => {
      return {
        ...service,
        selected: true
      };
    });
  dispatch(setQuoteReadOnlyServices(allReadOnlyServices));
  dispatch(setQuoteConfirmationId(appointment.quoteConfirmationCode));
};

export {
  setContactInfo,
  setFirstName,
  setLastName,
  setEmail,
  setPhoneNumber,
  setMobilePhoneNumber,
  setObfuscatedBy,
  setPersonId,
  setExtId
};



// WEBPACK FOOTER //
// ./src/store/reducers/appointment/actions.js